import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { NgModel, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { GlobalConstants } from 'src/app/services/global-constants';
import { SafeHTMLPipeStandAlone } from 'src/app/misc/pipes/safe-html-standalone.pipe';
import { TranslatorPipeStandAlone } from 'src/app/services/translator.pipe.standalone';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { RatingModule } from 'primeng/rating';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { TreeSelectModule } from 'primeng/treeselect';

@Component({
  selector: 'app-question-template-standalone',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SafeHTMLPipeStandAlone,
    TranslatorPipeStandAlone,
    RadioButtonModule,
    CheckboxModule,
    DropdownModule,
    RatingModule,
    CalendarModule,
    TableModule,
    TreeSelectModule,
  ],
  templateUrl: './question-template-standalone.component.html',
  styleUrls: ['./question-template-standalone.component.scss'],
})
export class QuestionTemplateComponentStandAlone {
  @ViewChild('questionDiv') questionDiv?: ElementRef;

  @ViewChildren(NgModel) fields!: QueryList<NgModel>; // Get all fields using NgModel
  public changes$ = new Subject<any>();
  //Current Question
  @Input() question: any;
  @Input() disabled: boolean = false;
  @Input() dActive:  boolean = true;
  @Input() isAssessment: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isRequiredColor: boolean = false;
  /** OU tree options for OU_TREE_DROPDOWN (attempt view) */
  @Input() ouTreeOptions: any[] = [];
  @Output() conditionalQuestions: EventEmitter<any> = new EventEmitter<any>();

  //To access static attets from server
  staticFileUrl: string = GlobalConstants.staticFilesURL;

  //Date selection dropdown options
  days: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  months: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
  years: number[] = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  //Dragged question in drag and drop question type
  draggedQuestion: any;

  dragStart(product: any) {
    this.draggedQuestion = product;
  }

  drop(question: any) {
    if (this.draggedQuestion) {
      question.meta.orderedFields = [
        ...question.meta.orderedFields,
        this.draggedQuestion,
      ];
      question.meta.fields = question.meta.fields.filter(
        (val: any) => val.label != this.draggedQuestion.label
      );
      this.draggedQuestion = null;
    }
  }

  dragEnd() {
    this.draggedQuestion = null;
  }

  addOrRemoveImageFromList(question: any, label: string) {
    if (question.meta.value.includes(label)) {
      let index = question.meta.value.indexOf(label);
      question.meta.value.splice(index, 1);
    } else {
      question.meta.value.push(label);
    }
    this.checkConditionalQuestion();
  }

  checkConditionalQuestion() {
    this.conditionalQuestions.emit();
  }

  onOuTreeSelect(node: any) {
    if (this.question?.meta) {
      this.question.meta.value = node?._id || node?.key || '';
    }
    this.checkConditionalQuestion();
  }

  /** Filter OU tree by question.meta.ouTreeTags for attempt dropdown */
  getOuTreeOptionsForQuestion(): any[] {
    const tree = this.ouTreeOptions || [];
    const tags = this.question?.meta?.ouTreeTags;
    if (!Array.isArray(tags) || tags.length === 0) {
      return tree;
    }
    return this.filterOuTreeByTag(tree, tags);
  }

  private filterOuTreeByTag(nodes: any[], tag: string[]): any[] {
    if (!nodes || !nodes.length) return [];
    return nodes
      .map((node: any) => this.filterOuNode(node, tag))
      .filter((n: any) => n !== null);
  }

  private filterOuNode(node: any, tag: string[]): any | null {
    if (!node) return null;
    const childCopy = node.children && node.children.length
      ? this.filterOuTreeByTag(node.children, tag)
      : [];
    if (tag.includes(node.tag) || childCopy.length > 0) {
      return { ...node, children: childCopy };
    }
    return null;
  }

  ngAfterViewInit(): void {
    // Subscribe to value changes of all fields

    this.fields.changes.subscribe((d) => {
      this.subscribeFieldChange()
    });

    this.subscribeFieldChange()
  }

  subscribeFieldChange() {
    this.fields.forEach((field: any) => {
      field.valueChanges?.subscribe(() => {
        this.changes$.next(true);
      });
    });
  }

  getRankDDValues(field: any, value: any, meta: any) {
    let values: any[] = [];

    meta.fields.forEach((metaField: any, i: number) => {
      if (field.label == metaField.label) {
        metaField.value = value;
      }
      values.push({
        label: (i + 1).toString(),
        value: i + 1,
        disabled: metaField.value == i + 1,
      });
    });

    meta.options = [
      { label: '-Select-', value: -1, disabled: false },
      ...values,
    ];
    meta.options = this.isAssessment
      ? values
      : [{ label: '-Select-', value: -1, disabled: false }, ...values];
  }

  getRankDDValues2(question: any): string[] {
    let values: string[] = [];

    for (let i = 1; i < question.meta.fields.length + 1; i++) {
      values.push(i.toString());
    }

    return values;
  }

  addRemoveValueInFieldArray(field: any, value: any) {
    if (field.value.includes(value)) {
      field.value = field.value.filter((v: string) => v != value);
      return;
    }
    field.value = [...field.value, value];
  }

  handleClick(question: any, field: any): void {
    if (!this.disabled) {
      question.meta.value = field.label;
      this.checkConditionalQuestion();
    }
  }

  getDatePlaceholder(date: string) {
    if (date) {
      return date.split('T')[0];
    }
    return '';
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (
      (charCode !== 46 &&
        charCode !== 45 &&
        charCode > 31 &&
        (charCode < 48 || charCode > 57)) ||
      (event.target.value.includes('.') && charCode === 46)
    ) {
      return false;
    }
    return true;
  }
}
